/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { type ClinicListItem } from '../../lib/api/sa.js';
import { formatInr } from '../../lib/utils/money.js';

const STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  active: 'green',
  trial: 'violet',
  trialing: 'violet',
  suspended: 'rose',
  cancelled: 'slate',
  past_due: 'amber',
};

function toneFor(status: string): BadgeTone {
  return STATUS_TONE[status] ?? 'slate';
}

const DASH = '—';

interface ClinicRowProps {
  readonly clinic: ClinicListItem;
  readonly onOpen: (id: string) => void;
}

/** One row of the Super Admin → Clinics table. */
export function ClinicTableRow({ clinic, onOpen }: ClinicRowProps): JSX.Element {
  const { t } = useTranslation();
  const open = useCallback(() => {
    onOpen(clinic.id);
  }, [onOpen, clinic.id]);

  return (
    <tr onClick={open} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">{clinic.name}</td>
      <td className="px-5 py-3">
        <Badge
          label={t(`sa.clinicStatus.${clinic.status}`, clinic.status)}
          tone={toneFor(clinic.status)}
        />
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{clinic.planName ?? DASH}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{formatInr(clinic.mrrPaise)}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{clinic.therapistCount}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{clinic.activeClientCount}</td>
    </tr>
  );
}
